import { loadConfig } from "../config";
import { forceRenewal, getValidIdToken } from "./session";

export { TOKEN_KEY } from "./session";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly bodyText: string,
  ) {
    super(`API error ${status}`);
  }
}

async function send(
  method: string,
  path: string,
  body: unknown,
  token: string | null,
): Promise<Response> {
  const cfg = await loadConfig();
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["content-type"] = "application/json";
  if (token) headers["authorization"] = `Bearer ${token}`;

  return fetch(`${cfg.apiBaseUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await getValidIdToken();
  let res = await send(method, path, body, token);

  // A token can be rejected before its own `exp` — renew once and retry rather
  // than bouncing a signed-in user out to the login page.
  if (res.status === 401 && token) {
    const renewed = await forceRenewal();
    if (renewed) res = await send(method, path, body, renewed);
  }

  if (!res.ok) throw new ApiError(res.status, await res.text());
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * The message to show a user for a failed request. The API sends
 * `{ error, message? }`; anything else falls back to the caller's wording.
 */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (!(e instanceof ApiError)) return fallback;
  try {
    const body = JSON.parse(e.bodyText) as { message?: unknown };
    if (typeof body.message === "string" && body.message) return body.message;
  } catch {
    // Non-JSON body — fall through to the caller's wording.
  }
  return fallback;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};
